/**
 * Context Thermostat — the Page math, mirrored from the backend (#349).
 *
 * The server is the authority: it ships the contract (`tokens_per_page`,
 * `min_pages`, `max_pages`, `high_cost_pages`, the default) on every payload
 * that carries a budget, and it normalizes anything it is sent. These helpers
 * exist so the SLIDER can be drawn and driven without re-deriving that contract
 * — they take the server's limits as an argument rather than hardcoding them,
 * so a change on the server does not need a matching change here.
 *
 * The slider's domain is an INDEX, not tokens: one position per Page from
 * `min_pages` to `max_pages`, plus ONE final position for Total Recall. That is
 * what makes "snaps every 750 tokens" and "arrow keys move one Page" free — the
 * native range input's own step is the snap.
 */

/** The stored sentinel for "hold the whole thread" (mirrors the backend). */
export const TOTAL_RECALL = 0;

/** Fallback contract, used only until the server's own limits arrive. */
export const DEFAULT_LIMITS = {
    tokens_per_page: 750,
    min_pages: 10,
    max_pages: 100,
    high_cost_pages: 30,
    default_tokens: 13500,
    default_pages: 18,
};

/** Roughly how many words a Page of memory is — the card's "≈560 words". */
export const WORDS_PER_PAGE = 560;

const int = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : fallback;
};

export function limitsOf(limits) {
    const l = limits || {};
    return {
        tokensPerPage: int(l.tokens_per_page, DEFAULT_LIMITS.tokens_per_page),
        minPages: int(l.min_pages, DEFAULT_LIMITS.min_pages),
        maxPages: int(l.max_pages, DEFAULT_LIMITS.max_pages),
        highCostPages: int(l.high_cost_pages, DEFAULT_LIMITS.high_cost_pages),
        defaultTokens: int(l.default_tokens, DEFAULT_LIMITS.default_tokens),
    };
}

/** The last slider index — the Total Recall position. */
export function maxIndex(limits) {
    const { minPages, maxPages } = limitsOf(limits);
    return maxPages - minPages + 1;
}

/** Slider index → stored tokens (0 = Total Recall). */
export function tokensFromIndex(index, limits) {
    const { tokensPerPage, minPages } = limitsOf(limits);
    const top = maxIndex(limits);
    const i = Math.min(Math.max(Math.round(Number(index) || 0), 0), top);
    if (i >= top) return TOTAL_RECALL;
    return (minPages + i) * tokensPerPage;
}

/**
 * Stored tokens → slider index.
 *
 * `null`/undefined means "inherit", and the slider still has to point
 * somewhere: it points at whatever is actually in force, which the caller
 * passes as `fallbackTokens` (the effective budget).
 */
export function indexFromTokens(tokens, limits, fallbackTokens) {
    const { tokensPerPage, minPages } = limitsOf(limits);
    const top = maxIndex(limits);
    const raw = tokens === null || tokens === undefined ? fallbackTokens : tokens;
    if (raw === TOTAL_RECALL) return top;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return top;
    const pages = Math.round(n / tokensPerPage);
    return Math.min(Math.max(pages - minPages, 0), top - 1);
}

/** Whole Pages for a token budget, or null for Total Recall / unset. */
export function pagesFromTokens(tokens, limits) {
    const { tokensPerPage } = limitsOf(limits);
    if (!tokens || Number(tokens) <= 0) return null;
    return Math.max(1, Math.round(Number(tokens) / tokensPerPage));
}

export function isTotalRecall(tokens) {
    return tokens === TOTAL_RECALL;
}

/**
 * Does this value need the High Energy Usage confirmation?
 * Above `high_cost_pages`, and Total Recall always.
 */
export function isHighCost(tokens, limits) {
    if (isTotalRecall(tokens)) return true;
    const pages = pagesFromTokens(tokens, limits);
    return pages !== null && pages > limitsOf(limits).highCostPages;
}

/** Approximate words of memory — the unit the tooltip explains Pages in. */
export function wordsForTokens(tokens, limits) {
    const pages = pagesFromTokens(tokens, limits);
    return pages === null ? null : pages * WORDS_PER_PAGE;
}

/**
 * Where to draw tick marks, as fractions of the track (0..1).
 *
 * One per DECADE of Pages plus the Total Recall end, rather than one per snap:
 * 92 hairlines two pixels apart is a solid bar, not a scale. The snapping is
 * real either way — it is the input's step, not the ticks.
 */
export function tickFractions(limits) {
    const { minPages, maxPages } = limitsOf(limits);
    const top = maxIndex(limits);
    const out = [];
    for (let pages = minPages; pages <= maxPages; pages += 10) {
        out.push((pages - minPages) / top);
    }
    if (out[out.length - 1] !== 1) out.push(1);
    return out;
}
